/**
 * Product Media Gallery
 * Handles carousel, thumbnail interactions, and zoom functionality
 */

class ProductMediaGallery {
  constructor(container) {
    this.container = container;
    this.layout = container.dataset.layout;
    this.isCarousel = container.dataset.carousel === 'true';
    this.enableZoom = container.dataset.enableZoom === 'true';
    this.swiper = null;
    this.isMobile = window.innerWidth < 990;
    
    this.init();
  }

  init() {
    if (this.isCarousel) {
      this.initCarousel();
    } else if (this.isThumbnailsLayout()) {
      this.initThumbnails();
    }
    
    // Initialize zoom after a short delay to ensure images are loaded
    if (this.enableZoom) {
      setTimeout(() => {
        this.initZoom();
      }, 100);
    }
    
    // Update on window resize
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 990;
    });
  }

  isThumbnailsLayout() {
    return ['thumbnails_bottom', 'thumbnails_left', 'thumbnails_right'].includes(this.layout);
  }

  initCarousel() {
    const swiperEl = this.container.querySelector('.product-gallery__swiper');
    if (!swiperEl) return;

    this.waitForSwiper(() => {
      this.createCarousel(swiperEl);
    });
  }

  waitForSwiper(callback) {
    if (typeof Swiper !== 'undefined') {
      callback();
      return;
    }

    if (window.__swiperLoading) {
      const checkInterval = setInterval(() => {
        if (typeof Swiper !== 'undefined') {
          clearInterval(checkInterval);
          callback();
        }
      }, 100);
      
      setTimeout(() => clearInterval(checkInterval), 5000);
      return;
    }

    window.__swiperLoading = true;
    
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
    script.onload = () => {
      window.__swiperLoading = false;
      callback();
    };
    document.head.appendChild(script);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
    document.head.appendChild(link);
  }

  createCarousel(swiperEl) {
    const slides = swiperEl.querySelectorAll('.swiper-slide');
    const isSingleSlide = slides.length === 1;
    
    const slidesPerView = this.layout === 'two_columns' ? 1 : 1;
    const slidesPerViewDesktop = this.layout === 'two_columns' ? 2 : 1;
    const spaceBetween = this.layout === 'two_columns' ? 16 : 16;

    const config = {
      slidesPerView: slidesPerView,
      spaceBetween: 16,
      loop: !isSingleSlide && slides.length > 2,
      watchOverflow: true,
      navigation: {
        prevEl: this.container.querySelector('.product-gallery__btn--prev'),
        nextEl: this.container.querySelector('.product-gallery__btn--next'),
      },
      pagination: {
        el: this.container.querySelector('.product-gallery__pagination'),
        clickable: true,
        dynamicBullets: false,
      },
      breakpoints: {
        990: {
          slidesPerView: slidesPerViewDesktop,
          spaceBetween: spaceBetween,
        },
      },
      on: {
        init: () => {
          this.container.setAttribute('data-initialized', 'true');
        },
      },
    };

    try {
      this.swiper = new Swiper(swiperEl, config);
    } catch (error) {
      console.error('Error initializing Swiper:', error);
    }
  }

  initThumbnails() {
    const thumbnails = this.container.querySelectorAll('[data-thumbnail]');
    const mainImages = this.container.querySelectorAll('[data-gallery-main] .product-gallery__media');
    
    if (!thumbnails.length || !mainImages.length) return;

    // Ensure first thumbnail and image are active on init
    this.setActiveThumbnail(thumbnails[0], mainImages);

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        this.setActiveThumbnail(thumbnail, mainImages);
      });
    });
  }

  setActiveThumbnail(activeThumbnail, mainImages) {
    const mediaId = activeThumbnail.dataset.mediaId;
    const thumbnails = this.container.querySelectorAll('[data-thumbnail]');
    
    // Update thumbnails
    thumbnails.forEach(t => t.classList.remove('product-gallery__thumbnail--active'));
    activeThumbnail.classList.add('product-gallery__thumbnail--active');
    
    // Update main images
    mainImages.forEach((image) => {
      if (image.dataset.mediaId === mediaId) {
        image.classList.add('product-gallery__media--active');
        image.style.display = 'block';
        image.style.opacity = '1';
        image.style.position = 'relative';
      } else {
        image.classList.remove('product-gallery__media--active');
        image.style.display = 'none';
        image.style.opacity = '0';
        image.style.position = 'absolute';
      }
    });
  }

  initZoom() {
    if (this.isMobile) {
      this.initMobileZoom();
    } else {
      this.initDesktopZoom();
    }
  }

  initDesktopZoom() {
    const images = this.container.querySelectorAll('.product-gallery__image');
    console.log('[Zoom Desktop] Encontradas', images.length, 'imágenes');
    
    if (images.length === 0) return;
    
    images.forEach((img, index) => {
      // Wait for image to load
      if (!img.complete) {
        img.addEventListener('load', () => {
          this.setupDesktopZoom(img, index);
        });
      } else {
        this.setupDesktopZoom(img, index);
      }
    });
  }

  setupDesktopZoom(img, index) {
    const wrapper = img.closest('.product-gallery__media-wrapper') || img.parentElement;
    if (!wrapper) return;
    
    console.log('[Zoom Desktop] Configurando zoom para imagen', index);
    
    // Ensure wrapper has proper positioning
    if (getComputedStyle(wrapper).position === 'static') {
      wrapper.style.position = 'relative';
    }
    
    wrapper.classList.add('product-gallery__zoom-container');
    
    // Create zoom lens
    const lens = document.createElement('div');
    lens.className = 'product-gallery__zoom-lens';
    wrapper.appendChild(lens);
    
    // Create zoom result window
    const result = document.createElement('div');
    result.className = 'product-gallery__zoom-result';
    const resultImg = document.createElement('img');
    resultImg.src = img.src;
    resultImg.alt = img.alt || '';
    result.appendChild(resultImg);
    wrapper.appendChild(result);
    
    // Zoom on mousemove
    wrapper.addEventListener('mouseenter', () => {
      console.log('[Zoom Desktop] Mouse enter');
      lens.classList.add('active');
      result.classList.add('active');
    });
    
    wrapper.addEventListener('mouseleave', () => {
      lens.classList.remove('active');
      result.classList.remove('active');
    });
    
    wrapper.addEventListener('mousemove', (e) => {
      this.handleDesktopZoom(e, img, lens, resultImg, result);
    });
  }

  handleDesktopZoom(e, img, lens, resultImg, result) {
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Position lens
    const lensWidth = lens.offsetWidth / 2;
    const lensHeight = lens.offsetHeight / 2;
    let lensX = x - lensWidth;
    let lensY = y - lensHeight;
    
    // Keep lens within image bounds
    lensX = Math.max(0, Math.min(lensX, rect.width - lens.offsetWidth));
    lensY = Math.max(0, Math.min(lensY, rect.height - lens.offsetHeight));
    
    lens.style.left = lensX + 'px';
    lens.style.top = lensY + 'px';
    
    // Calculate zoom
    const zoomLevel = 2.5;
    const resultWidth = result.offsetWidth;
    const resultHeight = result.offsetHeight;
    const imgWidth = rect.width;
    const imgHeight = rect.height;
    
    // Set result image size
    resultImg.style.width = (imgWidth * zoomLevel) + 'px';
    resultImg.style.height = (imgHeight * zoomLevel) + 'px';
    
    // Calculate position proportionally
    const percentX = lensX / imgWidth;
    const percentY = lensY / imgHeight;
    
    const moveX = percentX * (imgWidth * zoomLevel - resultWidth);
    const moveY = percentY * (imgHeight * zoomLevel - resultHeight);
    
    resultImg.style.left = -moveX + 'px';
    resultImg.style.top = -moveY + 'px';
  }

  initMobileZoom() {
    const images = this.container.querySelectorAll('.product-gallery__image');
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'product-gallery__zoom-overlay';
    overlay.setAttribute('data-zoom-overlay', '');
    
    const overlayContent = document.createElement('div');
    overlayContent.className = 'product-gallery__zoom-overlay-content';
    
    const overlayImg = document.createElement('img');
    overlayImg.className = 'product-gallery__zoom-image';
    overlayContent.appendChild(overlayImg);
    
    const closeBtn = document.createElement('button');
    closeBtn.className = 'product-gallery__zoom-close';
    closeBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    closeBtn.setAttribute('aria-label', 'Cerrar zoom');
    overlay.appendChild(closeBtn);
    
    const instructions = document.createElement('div');
    instructions.className = 'product-gallery__zoom-instructions';
    instructions.textContent = 'Pellizca para hacer zoom • Toca para cerrar';
    overlay.appendChild(instructions);
    
    overlay.appendChild(overlayContent);
    document.body.appendChild(overlay);
    
    // Zoom state
    let scale = 1;
    let lastDistance = 0;
    let posX = 0;
    let posY = 0;
    let lastPosX = 0;
    let lastPosY = 0;
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    
    // Reset zoom state
    const resetZoom = () => {
      scale = 1;
      posX = 0;
      posY = 0;
      overlayImg.style.transform = 'translate(0px, 0px) scale(1)';
    };
    
    // Update transform
    const updateTransform = () => {
      overlayImg.style.transform = `translate(${posX}px, ${posY}px) scale(${scale})`;
    };
    
    // Open zoom on image click
    images.forEach((img) => {
      img.addEventListener('click', (e) => {
        e.stopPropagation();
        overlayImg.src = img.src;
        overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
        resetZoom();
        
        // Show instructions briefly
        setTimeout(() => {
          instructions.style.animation = 'fadeInOut 4s ease-in-out';
        }, 100);
      });
    });
    
    // Close zoom
    const closeZoom = () => {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
      resetZoom();
    };
    
    // Close button
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeZoom();
    });
    
    // Click overlay to close (only when not zoomed)
    overlay.addEventListener('click', (e) => {
      if (scale <= 1.1) {
        closeZoom();
      }
    });
    
    // Prevent clicks on content from closing when zoomed
    overlayContent.addEventListener('click', (e) => {
      if (scale > 1.1) {
        e.stopPropagation();
      }
    });
    
    // Touch start
    overlayImg.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        // Pinch zoom start
        lastDistance = this.getDistance(e.touches[0], e.touches[1]);
      } else if (e.touches.length === 1 && scale > 1) {
        // Pan start
        isDragging = true;
        startX = e.touches[0].clientX - posX;
        startY = e.touches[0].clientY - posY;
      }
    });
    
    // Touch move
    overlayImg.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        // Pinch zoom
        e.preventDefault();
        const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
        const delta = currentDistance - lastDistance;
        scale += delta * 0.01;
        scale = Math.max(1, Math.min(scale, 4));
        lastDistance = currentDistance;
        updateTransform();
      } else if (e.touches.length === 1 && isDragging && scale > 1) {
        // Pan
        e.preventDefault();
        posX = e.touches[0].clientX - startX;
        posY = e.touches[0].clientY - startY;
        
        // Constrain panning
        const maxX = (overlayImg.offsetWidth * scale - overlayImg.offsetWidth) / 2;
        const maxY = (overlayImg.offsetHeight * scale - overlayImg.offsetHeight) / 2;
        posX = Math.max(-maxX, Math.min(maxX, posX));
        posY = Math.max(-maxY, Math.min(maxY, posY));
        
        updateTransform();
      }
    });
    
    // Touch end
    overlayImg.addEventListener('touchend', (e) => {
      isDragging = false;
      
      // Auto reset if barely zoomed
      if (scale < 1.1) {
        scale = 1;
        posX = 0;
        posY = 0;
        updateTransform();
      }
    });
    
    // Prevent default touch behavior on overlay
    overlay.addEventListener('touchmove', (e) => {
      if (scale > 1) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    // Clean up zoom elements
    const overlay = document.querySelector('.product-gallery__zoom-overlay');
    if (overlay) {
      overlay.remove();
    }
  }
}

// Initialize galleries
function initProductGalleries(scope = document) {
  scope.querySelectorAll('[data-product-gallery]').forEach((container) => {
    if (container.__productGallery) {
      container.__productGallery.destroy();
    }
    container.__productGallery = new ProductMediaGallery(container);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductGalleries());
} else {
  initProductGalleries();
}

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initProductGalleries(event.target);
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector('[data-product-gallery]');
    if (container && container.__productGallery) {
      container.__productGallery.destroy();
      container.__productGallery = null;
    }
  });
}
